// Wire mirrors of RataExport.Models.Conversion — keep in sync with the C# DTOs.

export interface ModeDto {
  id: string;
  label: string;
  maxInputFiles: number;
  inputNoun: string;
  contentNoun: string;
  extensions: string[];
}

export interface UploadedFileDto {
  name: string;
  length: number;
}

export interface IssueDto {
  severity: string;
  message: string;
}

export interface PreflightDto {
  canProceed: boolean;
  missing: string[];
  rejected: Record<string, string>;
  parametersDetected: string[];
  runsPerParameter: Record<string, number>;
  notUsedRunsPerParameter: Record<string, number>;
  issues: IssueDto[];
}

export interface CandidateDto {
  parameter: string;
  emitted: boolean;
}

export interface OutputDto {
  index: number;
  fileName: string;
  parameter: string;
  sha256?: string;
  hasOutput: boolean;
  auditFileName: string;
}

export interface ConversionSnapshotDto {
  id: string;
  modeId: string;
  state: string;
  files: UploadedFileDto[];
  preflight?: PreflightDto;
  failureReason?: string;
  extractionIssues: IssueDto[];
  candidates: CandidateDto[];
  outputs: OutputDto[];
}

export interface PrepareResultDto {
  status: string;
  failureReason?: string;
  preflight?: PreflightDto;
  extractionIssues: IssueDto[];
  candidates: CandidateDto[];
}

export interface FieldPromptDto {
  field: string;
  param?: string;
  level?: string;
  question: string;
  example?: string;
  defaultValue?: string;
  defaultSource?: string;
  isDate: boolean;
  wholeNumber: boolean;
  group?: string;
  note?: string;
  kind: 'text' | 'choice' | 'cemPaste';
  choices?: ChoiceDto[];
  expectedCount?: number;
}

export interface AffectedRunDto {
  runNumber?: number;
  originalValue: string;
}

export interface SubstitutionPromptDto {
  affectedRunCount: number;
  affectedRuns: AffectedRunDto[];
  defaultAccept: boolean;
  explanation: string;
}

export interface SheetQiDto {
  lastName?: string;
  firstName?: string;
  middleInitial?: string;
  examDate?: string;
}

export interface QiProfileDto {
  index: number;
  lastName?: string;
  firstName?: string;
  middleInitial?: string;
  examDate?: string;
  providerName?: string;
  providerEmail?: string;
}

export interface AetbProfileDto {
  index: number;
  name?: string;
  phone?: string;
  email?: string;
}

export interface ProfilesPromptDto {
  sheetQi?: SheetQiDto;
  qiProfiles: QiProfileDto[];
  aetbProfiles: AetbProfileDto[];
  defaultQiIndex?: number;
  defaultAetbIndex?: number;
}

export interface ReviewRowDto {
  section: string;
  label: string;
  value: string;
}

export interface RunRowDto {
  runNumber?: number;
  cemValue: string;
  referenceValue: string;
  load?: string;
  status?: string;
}

export interface ReviewSummaryDto {
  rows: ReviewRowDto[];
  averageGrossUnitLoad?: string;
  runs: RunRowDto[];
  loadAdvice: string;
  flowLevels?: FlowLevelSummaryDto[];
}

export interface FlowLevelSummaryDto {
  axis: string;
  name: string;
  headline: string;
  runs: RunRowDto[];
  notes: string[];
}

export interface ChoiceDto {
  value: string;
  label: string;
}

export interface DecisionPromptDto {
  id: string;
  reason: string;
  explanation: string;
  kind: 'confirm' | 'choice' | 'field' | 'impossible';
  question?: string;
  choices?: ChoiceDto[];
  field?: FieldPromptDto;
}

export interface StatDto {
  label: string;
  value: string;
}

export interface EmitResultDto {
  status: string;
  outputFileName?: string;
  sha256?: string;
  rataVerdict?: string;
  statistics: StatDto[];
  errors: string[];
  next?: ScreenDto;
}

export interface ScreenDto {
  kind: string;
  title: string;
  step: number;
  totalSteps: number;
  parameter: string;
  notes: string[];
  fields: FieldPromptDto[];
  substitution?: SubstitutionPromptDto;
  profiles?: ProfilesPromptDto;
  review?: ReviewSummaryDto;
  decisions: DecisionPromptDto[];
  result?: EmitResultDto;
}

export interface AnswerDto {
  field: string;
  param?: string;
  level?: string;
  value?: string;
}

export interface RunLoadDto {
  runNumber: number;
  load: string;
}

export interface DecisionAnswerDto {
  id: string;
  value?: string;
}

export interface SubmitScreenDto {
  answers?: AnswerDto[];
  unitOrStack?: string;
  substitutionAccepted?: boolean;
  qiProfileIndex?: number;
  qiFromSheet?: boolean;
  qiCustom?: QiProfileDto;
  aetbProfileIndex?: number;
  aetbCustom?: AetbProfileDto;
  action?: string;
  averageGrossUnitLoad?: string;
  runLoads?: RunLoadDto[];
  decisions?: DecisionAnswerDto[];
  acceptWarnings?: boolean;
}

export interface FieldProblemDto {
  field: string;
  param?: string;
  level?: string;
  message: string;
  isWarning: boolean;
}

export interface SubmitResultDto {
  problems: FieldProblemDto[];
  next?: ScreenDto;
}

export const fieldKey = (f: { field: string; param?: string | null; level?: string | null }) =>
  `${f.field}|${f.param ?? ''}|${f.level ?? ''}`;
